<template>
    <div class="min-h-screen bg-gray-100 p-6">
        <div class="max-w-6xl mx-auto">
            <!-- Header -->
            <div class="bg-white rounded-lg shadow-md p-6 mb-6">
                <div class="flex justify-between items-center">
                    <div>
                        <h1 class="text-3xl font-bold text-gray-800">Dashboard Caisse</h1>
                        <p class="text-gray-600">{{ currentRestaurant.name }}</p>
                    </div>
                    <div class="flex space-x-4">
                        <router-link
                                to="/restaurantselector"
                                class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
                        >
                            Changer de restaurant
                        </router-link>
                        <Notifications />
                    </div>
                </div>

                <!-- Cash Register Control -->
                <div class="mt-4 p-4 rounded-lg" :class="cashRegisterStatusClass">
                    <div class="flex justify-between items-center">
                        <div>
                            <p class="font-semibold text-lg">
                                Statut de la caisse: {{ currentCashRegister ? currentCashRegister.status : 'N/A' }}
                            </p>
                            <p v-if="currentCashRegister?.status === 'closed'" class="text-sm mt-1">
                                La caisse doit être ouverte pour permettre la prise de commande
                            </p>
                        </div>
                        <button
                                @click="toggleCashRegister"
                                class="px-6 py-3 rounded-lg font-semibold transition-colors"
                                :class="currentCashRegister?.status === 'open'
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-green-600 text-white hover:bg-green-700'"
                        >
                            {{ currentCashRegister?.status === 'open' ? 'Fermer la Caisse' : 'Ouvrir la Caisse' }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Active Orders -->
            <div class="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 class="text-2xl font-semibold mb-4">Commandes Actives</h2>

                <div v-if="activeOrders.length === 0" class="text-gray-500 text-center py-8">
                    Aucune commande active
                </div>

                <div v-else class="space-y-4">
                    <div
                            v-for="order in activeOrders"
                            :key="order.id"
                            class="border border-gray-200 rounded-lg p-4"
                    >
                        <div class="flex justify-between items-start mb-4">
                            <div>
                                <p class="font-semibold text-lg">Table {{ getTableNumber(order.tableId) }}</p>
                                <p class="text-gray-600">Commande #{{ order.id }}</p>
                                <p class="text-sm text-gray-500">
                                    Serveur: {{ getServerName(order.serverId) }}
                                </p>
                            </div>
                            <div class="text-right">
                                <p class="text-lg font-semibold">{{ order.total }}€</p>
                                <p class="text-sm text-gray-500">
                                    {{ formatDate(order.createdAt) }}
                                </p>
                            </div>
                        </div>

                        <!-- Order Items -->
                        <div class="mb-4">
                            <h3 class="font-semibold mb-2">Articles:</h3>
                            <div class="space-y-2">
                                <div
                                        v-for="item in order.items"
                                        :key="item.id"
                                        class="flex justify-between items-center p-2 bg-gray-50 rounded"
                                >
                                    <div>
                                        <span class="font-medium">{{ item.name }}</span>
                                        <span class="text-gray-600 ml-2">x{{ item.quantity }}</span>
                                        <span class="ml-4 text-sm px-2 py-1 rounded" :class="getStatusClass(item.status)">
                      {{ item.status }}
                    </span>
                                    </div>
                                    <span class="font-semibold">{{ (item.price * item.quantity).toFixed(2) }}€</span>
                                </div>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="flex justify-between items-center">
                            <div>
                <span class="text-sm text-gray-600">
                  Tous les articles servis:
                  <span :class="allItemsServed(order) ? 'text-green-600' : 'text-orange-600'">
                    {{ allItemsServed(order) ? 'Oui' : 'Non' }}
                  </span>
                </span>
                            </div>
                            <button
                                    @click="processPayment(order)"
                                    :disabled="!allItemsServed(order)"
                                    class="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                Procéder au paiement
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Order History -->
            <div class="bg-white rounded-lg shadow-md p-6">
                <h2 class="text-2xl font-semibold mb-4">Historique des Commandes Payées</h2>

                <div v-if="paidOrders.length === 0" class="text-gray-500 text-center py-8">
                    Aucune commande payée aujourd'hui
                </div>

                <div v-else class="space-y-3">
                    <div
                            v-for="order in paidOrders"
                            :key="order.id"
                            class="border border-gray-200 rounded-lg p-4"
                    >
                        <div class="flex justify-between items-center">
                            <div>
                                <p class="font-semibold">Table {{ getTableNumber(order.tableId) }}</p>
                                <p class="text-sm text-gray-600">Commande #{{ order.id }}</p>
                            </div>
                            <div class="text-right">
                                <p class="font-semibold">{{ order.total }}€</p>
                                <p class="text-sm text-gray-500">
                                    Payé le {{ formatDate(order.updatedAt) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapGetters, mapActions } from 'vuex';
    import Notifications from './notifications.vue';

    export default {
        name: 'CaisseDashboard',
        components: {
            Notifications
        },
        computed: {
            ...mapState(['currentRestaurant', 'orders', 'tables', 'servers']),
            ...mapGetters(['currentCashRegister', 'currentRestaurantOrders']),
            cashRegisterStatusClass() {
                return this.currentCashRegister?.status === 'open'
                    ? 'bg-green-100 text-green-800 border border-green-200'
                    : 'bg-red-100 text-red-800 border border-red-200';
            },
            activeOrders() {
                return this.currentRestaurantOrders.filter(order => order.status === 'active' && !order.paid);
            },
            paidOrders() {
                return this.currentRestaurantOrders.filter(order => order.paid);
            }
        },
        methods: {
            ...mapActions(['toggleCashRegister', 'updateItemStatus']),
            getTableNumber(tableId) {
                const table = this.tables.find(t => t.id === tableId);
                return table ? table.number : 'N/A';
            },
            getServerName(serverId) {
                const server = this.servers.find(s => s.id === serverId);
                return server ? server.name : 'N/A';
            },
            getStatusClass(status) {
                const classes = {
                    'pending': 'bg-orange-100 text-orange-800',
                    'in_progress': 'bg-blue-100 text-blue-800',
                    'ready': 'bg-green-100 text-green-800',
                    'served': 'bg-gray-100 text-gray-800'
                };
                return classes[status] || 'bg-gray-100 text-gray-800';
            },
            allItemsServed(order) {
                return order.items.every(item => item.status === 'served');
            },
            formatDate(date) {
                return new Date(date).toLocaleString('fr-FR');
            },
            toggleCashRegister() {
                if (this.currentCashRegister) {
                    this.toggleCashRegister(this.currentCashRegister.id);
                }
            },
            processPayment(order) {
                if (confirm(`Confirmer le paiement de ${order.total}€ pour la table ${this.getTableNumber(order.tableId)} ?`)) {
                    order.paid = true;
                    order.status = 'completed';
                    order.updatedAt = new Date();

                    // Libérer la table
                    const table = this.tables.find(t => t.id === order.tableId);
                    if (table) {
                        table.status = 'free';
                    }

                    alert('Paiement effectué avec succès !');
                }
            }
        }
    };
</script>